import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { createBrowserRouter } from 'react-router-dom';
import { useHost } from '../hooks/use-host';
import { usePageTitleDetailValue } from '../hooks/use-page-title-detail';
import { useCookieConsent } from '../contexts/cookie-consent-context';
import { buildDocumentTitle, resolveRouteTitle } from '../routes/route-title';
import { pushPageView } from '../util/push-page-view';
import { T } from '../util/Localize';

type ShellRouter = ReturnType<typeof createBrowserRouter>;
type SentView = { pathname: string; pageTitle: string };

/**
 * ROBO-932 QA: Site Summary, Delivery and Team Schedule replace their URL with a default sub-path
 * (`/Sites/5` → `/Sites/5/summary`), so one click sent two views. Waiting cannot tell that apart,
 * as Delivery redirects only once its data has loaded, and neither can REPLACE alone, as their tabs
 * navigate with REPLACE too. The shape of the move can: same page, one level further down the path.
 * The view already sent stands, so `page_path` is the path that was opened.
 */
function isDefaultSubPathRedirect(
    last: SentView | null,
    pathname: string,
    pageTitle: string,
    historyAction: string
): boolean {
    return (
        historyAction === 'REPLACE' &&
        last?.pageTitle === pageTitle &&
        pathname.startsWith(`${last.pathname}/`)
    );
}

/**
 * ROBO-932 — owns `document.title` for a shell, and tells GTM about each page.
 *
 * Rendered BESIDE `RouterProvider`, after `HostEnvironment` (so the host's locale is applied first),
 * and reads the router through `router.subscribe` rather than `useMatches` — the hub's routes have
 * no layout route to host a component inside the router.
 *
 * - Tab: "Record · Brand" when the page set a `usePageTitleDetail`, else "Page · Brand" (translated).
 * - Analytics: one `virtual_page_view` per PATHNAME, carrying the route's English title. A
 *   search-only change (opening `?panel=`) is not a page view (Herb, 2026-10-29). Accepting cookies
 *   mid-visit counts the page the visitor is on; pages seen before that are never sent.
 *   A redirect to a default sub-path is not a second view ({@link isDefaultSubPathRedirect}).
 */
export function RouteTitleAndPageView({ router }: { router: ShellRouter }) {
    const state = useSyncExternalStore(router.subscribe, () => router.state);
    const { data: hostInfo } = useHost();
    const { thirdPartyAllowed } = useCookieConsent();
    const detail = usePageTitleDetailValue();
    const lastSentRef = useRef<SentView | null>(null);

    const pathname = state.location.pathname;
    const historyAction = state.historyAction;
    const isIdle = state.navigation.state === 'idle';
    const pageTitle = resolveRouteTitle(state.matches);

    useEffect(() => {
        if (!isIdle) return;
        document.title = buildDocumentTitle({
            pageTitle: pageTitle ? T(pageTitle) : undefined,
            detail,
            brandName: hostInfo?.brandName
        });
    }, [isIdle, pageTitle, detail, hostInfo?.brandName, hostInfo?.culture]);

    useEffect(() => {
        if (!thirdPartyAllowed) {
            // Re-arm, so a later accept counts the page the visitor is on at that moment.
            lastSentRef.current = null;
            return;
        }
        const last = lastSentRef.current;
        if (!isIdle || !pageTitle || last?.pathname === pathname) return;
        lastSentRef.current = { pathname, pageTitle };
        if (isDefaultSubPathRedirect(last, pathname, pageTitle, historyAction)) return;
        void pushPageView(pathname, pageTitle);
    }, [thirdPartyAllowed, isIdle, pathname, pageTitle, historyAction]);

    return null;
}
